'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, styled } from '@mui/material';
import type { ParameterCheckResult } from '@repo/api-gw-sdk';
const ParameterCell = styled(TableCell)(() => ({
  borderBottom: '1px solid var(--mui-palette-divider)',
  paddingTop: '12px',
  paddingBottom: '12px',
  paddingLeft: 0,
  paddingRight: '24px',
  '&:last-child': {
    paddingRight: 0
  }
}));
const ParameterHeaderCell = styled(ParameterCell)(() => ({
  paddingTop: 0
}));

// A value the parameter group does not carry is prose, not a value, so it drops the monospace the
// real values keep.
const RequiredValueCell = t0 => {
  const $ = _c(3);
  const {
    param
  } = t0;
  if (param.unmeasured) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Typography variant="body1">N/A</Typography>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = param.isMinimum ? `\u2265${param.requiredValue}` : param.requiredValue;
  let t2;
  if ($[1] !== t1) {
    t2 = <Typography variant="body1"><code>{t1}</code></Typography>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

// The icon is the only place a row's detail is shown, so every state that has something to say
// carries it as the tooltip: what still has to happen before the row can be judged, or why it
// failed.
export const UNMEASURED_TOOLTIP = 'Finish SQL onboarding (step 2) first';
const statusIcon = (param: ParameterCheckResult) => {
  if (param.unmeasured) {
    return {
      className: 'material-symbols-schedule-outline',
      color: 'var(--mui-palette-custom-GREY-01)',
      tooltip: UNMEASURED_TOOLTIP
    };
  }
  if (param.ok) {
    return {
      className: 'material-symbols-check-circle-outline-rounded',
      color: 'var(--mui-palette-success-main)',
      tooltip: ''
    };
  }
  return {
    className: 'material-symbols-cancel-outline',
    color: 'var(--mui-palette-error-main)',
    tooltip: param.error ?? ''
  };
};
const StatusIcon = t0 => {
  const $ = _c(10);
  const {
    param
  } = t0;
  let t1;
  if ($[0] !== param) {
    t1 = statusIcon(param);
    $[0] = param;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    className,
    color,
    tooltip
  } = t1;
  const t2 = `${className} text-[18px]`;
  let t3;
  if ($[2] !== color) {
    t3 = {
      color
    };
    $[2] = color;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <Box component="i" className={t2} sx={t3} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4 || $[8] !== tooltip) {
    t5 = <Tooltip title={tooltip}>{t4}</Tooltip>;
    $[7] = t4;
    $[8] = tooltip;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const ParameterTableRow = t0 => {
  const $ = _c(15);
  const {
    param
  } = t0;
  let t1;
  if ($[0] !== param.parameterName) {
    t1 = <ParameterCell><Typography variant="body1"><code>{param.parameterName}</code></Typography></ParameterCell>;
    $[0] = param.parameterName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== param.currentValue) {
    t2 = param.currentValue ? <code>{param.currentValue}</code> : "Not set";
    $[2] = param.currentValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <ParameterCell><Typography variant="body1">{t2}</Typography></ParameterCell>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== param) {
    t4 = <ParameterCell><RequiredValueCell param={param} /></ParameterCell>;
    $[6] = param;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== param) {
    t5 = <ParameterCell><StatusIcon param={param} /></ParameterCell>;
    $[8] = param;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <TableRow>{t1}{t3}{t4}{t5}</TableRow>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export const ParameterTable = t0 => {
  const $ = _c(8);
  const {
    params
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ParameterHeaderCell><Typography variant="subtitle2">Parameter</Typography></ParameterHeaderCell>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ParameterHeaderCell><Typography variant="subtitle2">Current value</Typography></ParameterHeaderCell>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ParameterHeaderCell><Typography variant="subtitle2">Required value</Typography></ParameterHeaderCell>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableHead><TableRow>{t1}{t2}{t3}<ParameterHeaderCell><Typography variant="subtitle2">Status</Typography></ParameterHeaderCell></TableRow></TableHead>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== params) {
    t5 = params.map(_temp);
    $[4] = params;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <TableContainer><Table size="small">{t4}<TableBody>{t5}</TableBody></Table></TableContainer>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
};
function _temp(param) {
  return <ParameterTableRow key={param.parameterName} param={param} />;
}